import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Languages, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "@/lib/scroll";
import { getCompanies } from "@/data/content";
import { UI } from "@/i18n/copy";
import { useLang } from "@/i18n/LanguageContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, toggle } = useLang();
  const t = UI[lang].nav;
  const companies = getCompanies(lang);

  const LINKS = [
    { label: t.about, id: "about" },
    { label: t.companies, id: "companies" },
    { label: t.contact, id: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled
            ? "border-black/10 bg-white/90 shadow-[0_8px_30px_rgba(0,0,0,0.06)] backdrop-blur-xl"
            : "border-white/10 bg-black/30 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <button
            data-testid="nav-logo-btn"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label="Back to top"
            className={scrolled ? "text-black" : "text-white"}
          >
            <Logo dark={!scrolled} />
          </button>

          <nav className="hidden items-center gap-9 lg:flex" data-testid="nav-links">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`group relative text-[13px] font-semibold uppercase tracking-[0.18em] transition-colors ${
                  scrolled ? "text-black/70 hover:text-black" : "text-white/75 hover:text-white"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1.5 start-0 h-px w-0 transition-[width] duration-300 group-hover:w-full ${
                    scrolled ? "bg-black" : "bg-white"
                  }`}
                />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-lang-toggle"
              onClick={toggle}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[12px] font-bold transition-colors duration-300 ${
                scrolled
                  ? "border-black/15 text-black hover:bg-black hover:text-white"
                  : "border-white/25 text-white hover:bg-white hover:text-black"
              }`}
            >
              <Languages className="h-3.5 w-3.5" />
              {t.langToggle}
            </button>
            <button
              data-testid="nav-get-in-touch-btn"
              onClick={() => go("contact")}
              className={`group hidden items-center gap-2 rounded-full px-6 py-2.5 text-[13px] font-bold uppercase tracking-[0.14em] transition-colors duration-300 sm:inline-flex ${
                scrolled
                  ? "bg-black text-white hover:bg-black/80"
                  : "bg-white text-black hover:bg-white/85"
              }`}
            >
              {t.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className={`inline-flex h-11 w-11 items-center justify-center rounded-full border lg:hidden ${
                scrolled ? "border-black/15 text-black" : "border-white/25 text-white"
              }`}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-black text-white"
          >
            <div className="flex h-[72px] items-center justify-between px-5 sm:px-8">
              <Logo dark />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/25"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l.id)}
                  className="text-start text-4xl font-black tracking-tight hover:text-white/70 sm:text-5xl"
                >
                  {l.label}
                </motion.button>
              ))}
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
                {companies.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`mobile-nav-company-${c.id}`}
                    onClick={() => go(`company-${c.id}`)}
                    className="flex items-center gap-2 text-start text-sm font-semibold text-white/60 hover:text-white"
                  >
                    <span className="h-1.5 w-1.5 rotate-45" style={{ backgroundColor: c.color }} />
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-8 pb-8 font-mono-label text-[11px] uppercase tracking-[0.25em] text-white/40">
              {t.location}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
